import React, { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getMvcForLocation } from '../../offices/offices-selectors';
import moment from 'moment';
import { createLocation } from '../../offices/offices-actions';
import { mvcConfig } from '../configs/mvc-config';
import { getAdminEmails, getWriteTabsPermissions } from '../../../store/user/user-reducer';
import { useMsal } from '@azure/msal-react';
import { fetchAdminEmails } from 'apps/mod/src/store/user/user-actions';
import { formItemMap } from '@med-fe/ui';

function MvcDetails({ locId }) {
  const [form] = Form.useForm();
  const [isDisabled, setIsDisabled] = useState(true);
  const dispatch = useDispatch();
  const data = useSelector((state) => getMvcForLocation(state, locId));
  const isEditAllowed = useSelector(getWriteTabsPermissions).mvc;
  const [acuityLaunchDate, setAcuityLaunchDate] = useState(
    data?.acuityLogicLaunchDate ? data?.acuityLogicLaunchDate : null
  );
  const [isAcuityLaunchDateConfirmVisible, setAcuityLaunchDateConfirmVisible] = useState(false);
  const adminEmails = useSelector(getAdminEmails);
  const { accounts } = useMsal();
  const [allowDateClear, setAllowDateClear] = useState<boolean>(false);
  const hasMounted = useRef(false);

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    dispatch(fetchAdminEmails());
  }, []);

  const onLaunchDateSelect = (value) => {
    form.setFieldsValue({ acuityLogicLaunchDate: value ? value.format('YYYY-MM-DD') : null });
    setAcuityLaunchDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  useEffect(() => {
    setAllowDateClear(adminEmails?.some((user) => user?.trim().toLowerCase() === accounts[0]?.username));
  }, [adminEmails, accounts]);

  const onConfirmSetVisible = (value, name) => {
    if (name === 'Acuity Logic Launch Date') setAcuityLaunchDateConfirmVisible(value);
  };

  useEffect(() => {
    form.setFieldsValue({
      ...data,
      ...(!!data.acuityLogicLaunchDate && { acuityLogicLaunchDate: moment(data.acuityLogicLaunchDate) }),
      ...(!!data.legacyArClosed && { legacyArClosed: moment(data.legacyArClosed) }),
      ...(!!data.transitionArClosed && { transitionArClosed: moment(data.transitionArClosed) }),
      ...(!!data.cliaExpiration && { cliaExpiration: moment(data.cliaExpiration) }),
      ...(!!data.medicareDme && { medicareDme: moment(data.medicareDme) }),
    });
  }, [locId]);

  const onSubmit = (formData: any) => {
    const payload = {
      ...formData,
      ...(!!formData.acuityLogicLaunchDate && {
        acuityLogicLaunchDate: acuityLaunchDate,
      }),
      ...(!!formData.legacyArClosed && { legacyArClosed: formData.legacyArClosed.format('YYYY-MM-DD') }),
      ...(!!formData.transitionArClosed && { transitionArClosed: formData.transitionArClosed.format('YYYY-MM-DD') }),
      id: locId,
      ...(!!formData.cliaExpiration && { cliaExpiration: formData.cliaExpiration }),
      ...(!!formData.medicareDme && { medicareDme: formData.medicareDme }),
    };

    dispatch(createLocation(payload));
  };

  return (
    <Form
      form={form}
      layout='vertical'
      className='mvc-details-form'
      onFinish={(formData) => {
        onSubmit(formData);
        setIsDisabled(true);
      }}
      onValuesChange={() => setIsDisabled(false)}
    >
      {(() => {
        const entries = Object.entries(mvcConfig);
        const renderedGroups = new Set<string>();
        return entries.map(([key, value]: any) => {
          // Handle grouped items
          if (value.group) {
            if (renderedGroups.has(value.group)) return null;
            renderedGroups.add(value.group);
            const groupItems = entries.filter(([, v]: any) => v.group === value.group);
            return (
              <div key={value.group} style={{ display: 'flex', gap: '16px', width: '100%' }}>
                {groupItems.map(([gKey, gValue]: any) => {
                  const formItemParams = { ...gValue, allowDateClear };
                  const params = {
                    key: gKey,
                    label: gValue.label,
                    name: gKey,
                    ...(gValue.type === 'checkbox' && { valuePropName: 'checked' }),
                  };
                  return (
                    <Form.Item {...params} className='checkbox-item'>
                      {formItemMap(formItemParams, !isEditAllowed)}
                    </Form.Item>
                  );
                })}
              </div>
            );
          }
          const formItemParams = {
            ...value,
            allowDateClear: allowDateClear,
            ...(key === 'acuityLogicLaunchDate' && {
              onSelect: onLaunchDateSelect,
              data: acuityLaunchDate,
              onConfirmVisible: onConfirmSetVisible,
              isConfirmVisible: isAcuityLaunchDateConfirmVisible,
              name: value.label,
            }),
          };
          const params = {
            key,
            label: value.label,
            name: key,
            ...(value.type === 'checkbox' && { valuePropName: 'checked' }),
          };

          // For safetyVendors, display as labels instead of text field
          if (key === 'safetyVendors') {
            const vendorNames = data?.safetyVendors ? data.safetyVendors.split(', ') : [];
            return (
              <div key={key} className='safety-vendors-container'>
                <div className='ant-form-item-label'>
                  <label>{value.label}</label>
                </div>
                <div className='safety-vendors-list'>
                  {vendorNames.length > 0 ? (
                    vendorNames.map((vendor: string, index: number) => (
                      <div key={index} className='label-text'>
                        {vendor}
                      </div>
                    ))
                  ) : (
                    <div className='label-text'>-</div>
                  )}
                </div>
              </div>
            );
          }

          return (
            <Form.Item {...params} className={value.type === 'checkbox' ? 'checkbox-item' : ''}>
              {formItemMap(formItemParams, !isEditAllowed || value.disabled)}
            </Form.Item>
          );
        });
      })()}

      <Form.Item>
        <Button type='primary' htmlType='submit' className={'btn-form-action'} disabled={!isEditAllowed || isDisabled}>
          SAVE
        </Button>
      </Form.Item>
    </Form>
  );
}

export default MvcDetails;
